import { Runner, ParsedRunner } from '../types';

export const parseRunnerData = (runner: Runner): ParsedRunner => {
  try {
    const aiData = JSON.parse(runner.ai_data);
    return {
      ...runner,
      ai_data: aiData
    };
  } catch (error) {
    console.error('Error parsing AI data:', error);
    return {
      ...runner,
      ai_data: {
        horse: {
          id: runner.horse_id,
          name: runner.horse_name,
          total_runs: 0,
          best_distance: '',
          best_win_pct: 0,
          last_run: { date: '', course: '', pos: '', going: '' },
          recent_form: ''
        },
        trainer: {
          name: '',
          course_stats: { runners: 0, wins: 0, win_pct: 0, a_e: 0, place_return: 0 },
          distance_stats: { runners: 0, wins: 0, win_pct: 0, a_e: 0, place_return: 0 }
        },
        owner: {
          name: '',
          course_stats: { runners: 0, wins: 0, win_pct: 0, a_e: 0, place_return: 0 },
          distance_stats: { runners: 0, wins: 0, win_pct: 0, a_e: 0, place_return: 0 }
        },
        jockey: {
          name: '',
          course_stats: { runners: 0, wins: 0, win_pct: 0, a_e: 0, place_return: 0 },
          distance_stats: { runners: 0, wins: 0, win_pct: 0, a_e: 0, place_return: 0 }
        }
      }
    };
  }
};

export const formatTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':');
  return `${hours}:${minutes}`;
};

/** Join race class, type, distance, going — skips empty parts (e.g. IRE races with no class). */
export const formatRaceSpecs = (race: {
  race_class?: string | null;
  race_type?: string | null;
  distance?: string | null;
  going?: string | null;
}): string => {
  return [race.race_class, race.race_type, race.distance, race.going]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' - ');
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
  return `${day} ${month} ${year}`;
};

export const getConfidenceColor = (confidence: string): string => {
  switch (confidence.toLowerCase()) {
    case 'high':
      return '#10b981'; // green
    case 'medium':
      return '#f59e0b'; // amber
    case 'low':
      return '#ef4444'; // red
    default:
      return '#6b7280'; // gray
  }
};

export const getScoreColor = (score: number): string => {
  if (score >= 80) return '#10b981'; // green
  if (score >= 60) return '#f59e0b'; // amber
  return '#ef4444'; // red
};

export const calculatePotentialReturn = (stake: number, odds: string): number => {
  const decimalOdds = parseFloat(odds);
  return stake * decimalOdds;
};

export const calculateAverageOdds = (runners: Runner[]): number => {
  if (runners.length === 0) return 0;
  
  const totalOdds = runners.reduce((sum, runner) => {
    return sum + parseFloat(runner.bet365_decimal_odds);
  }, 0);
  
  return totalOdds / runners.length;
};

export const formatOdds = (odds: number): string => {
  return odds.toFixed(2);
};

/**
 * Normalizes horse names by removing bracketed content and extra spaces
 * Used for matching AI selections with non-runners
 */
export const normalizeHorseName = (name: string): string => {
  if (!name) return '';
  
  return name
    .trim()
    .replace(/\s*\([^)]*\)\s*/g, '') // Remove all bracketed content like (IRE), (self certificate, off feed)
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .toLowerCase();
};

/**
 * Checks if the AI selection was a non-runner by intelligently matching names
 */
export const isNonRunner = (aiSelection: string, nonRunnerField?: string): boolean => {
  if (!nonRunnerField || !aiSelection) return false;
  
  const normalizedSelection = normalizeHorseName(aiSelection);
  const normalizedNonRunner = normalizeHorseName(nonRunnerField);
  
  // Direct match after normalization
  if (normalizedSelection === normalizedNonRunner) return true;
  
  // Check if the non-runner field contains the selection name
  // This handles cases like "Maldini Milano (self certificate, off feed)" matching "Maldini Milano (IRE)"
  if (normalizedNonRunner.includes(normalizedSelection)) return true;
  
  // Check if the selection name contains the non-runner name
  if (normalizedSelection.includes(normalizedNonRunner)) return true;
  
  return false;
};
